import { sendMetaWebsiteEvent, type MetaWebsiteEventName, type MetaCustomEventName, type MetaContentName } from "../api/metaTracking";
import { config } from "../config";
import { getOrCreateTrackingUuid } from "../api/trackingIdentity";
import { getMetaIdentifiers } from "./metaIdentifiers";

export type MetaTrackingContext = {
  eventId: string;
  externalId: string | null;
  fbp: string | null;
  fbc: string | null;
};

export type PendingCheckoutTracking = {
  sessionId: string;
  eventId: string;
  planId: string;
  value: number;
  currency: string;
  createdAt: number;
};

type MetaPixelPurchaseEvent = {
  eventId: string;
  contentId: string;
  currency: string;
  value: number;
};

type FbqFunction = (
  command: "track" | "trackCustom",
  eventName: string,
  parameters?: Record<string, unknown>,
  options?: Record<string, unknown>
) => void;

type WindowWithMetaPixel = Window &
  typeof globalThis & {
    fbq?: FbqFunction;
  };

const PENDING_CHECKOUT_STORAGE_KEY = "parables:pending-checkout";
const TRACKED_PURCHASE_EVENT_IDS_STORAGE_KEY = "parables:tracked-purchase-events";
const TRACKED_CHECKOUT_EVENT_IDS_STORAGE_KEY = "parables:tracked-checkout-events";
const trackedPurchaseIds = new Set<string>();
const trackedCheckoutIds = new Set<string>();
const MAX_PENDING_CHECKOUT_AGE_MS = 12 * 60 * 60 * 1000;
const generateEventId = (): string => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
};

const normalizeEventId = (eventId: string | null | undefined): string | null => {
  if (!eventId) {
    return null;
  }

  const trimmed = eventId.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const getSessionStorage = (): Storage | null => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

const getFbq = (): FbqFunction | null => {
  if (!config.enableMarketing) return null;
  if (typeof window === "undefined") {
    return null;
  }

  const { fbq } = window as WindowWithMetaPixel;
  return typeof fbq === "function" ? fbq : null;
};

const readTrackedEventIds = (key: string): string[] => {
  const storage = getSessionStorage();
  if (!storage) {
    return [];
  }

  let rawValue: string | null;
  try { rawValue = storage.getItem(key); } catch { return []; }
  if (!rawValue) {
    return [];
  }

  try {
    const parsedValue = JSON.parse(rawValue);
    return Array.isArray(parsedValue)
      ? parsedValue.filter((value): value is string => typeof value === "string" && value.trim().length > 0)
      : [];
  } catch {
    return [];
  }
};

const wasPurchaseEventTracked = (eventId: string): boolean => {
  return trackedPurchaseIds.has(eventId) || readTrackedEventIds(TRACKED_PURCHASE_EVENT_IDS_STORAGE_KEY).includes(eventId);
};

const markPurchaseEventTracked = (eventId: string): void => {
  trackedPurchaseIds.add(eventId);
  const storage = getSessionStorage();
  if (!storage) {
    return;
  }

  const trackedEventIds = readTrackedEventIds(TRACKED_PURCHASE_EVENT_IDS_STORAGE_KEY);
  if (trackedEventIds.includes(eventId)) {
    return;
  }

  try { storage.setItem(TRACKED_PURCHASE_EVENT_IDS_STORAGE_KEY, JSON.stringify([...trackedEventIds, eventId])); } catch { /* Storage must never block fulfillment. */ }
};

const readPendingCheckoutTracking = (): PendingCheckoutTracking | null => {
  const storage = getSessionStorage();
  if (!storage) {
    return null;
  }

  const rawValue = storage.getItem(PENDING_CHECKOUT_STORAGE_KEY);
  if (!rawValue) {
    return null;
  }

  try {
    const parsedValue = JSON.parse(rawValue) as Partial<PendingCheckoutTracking>;
    if (
      typeof parsedValue.sessionId !== "string" ||
      typeof parsedValue.eventId !== "string" ||
      typeof parsedValue.planId !== "string" ||
      typeof parsedValue.value !== "number" ||
      typeof parsedValue.currency !== "string" ||
      typeof parsedValue.createdAt !== "number"
    ) {
      storage.removeItem(PENDING_CHECKOUT_STORAGE_KEY);
      return null;
    }

    if (Date.now() - parsedValue.createdAt > MAX_PENDING_CHECKOUT_AGE_MS) {
      storage.removeItem(PENDING_CHECKOUT_STORAGE_KEY);
      return null;
    }

    return parsedValue as PendingCheckoutTracking;
  } catch {
    storage.removeItem(PENDING_CHECKOUT_STORAGE_KEY);
    return null;
  }
};

export const getMetaTrackingContext = (): MetaTrackingContext => {
  if (!config.enableMarketing) return { eventId: generateEventId(), externalId: null, fbp: null, fbc: null };
  const { fbp, fbc } = getMetaIdentifiers();

  return {
    eventId: generateEventId(),
    externalId: getOrCreateTrackingUuid(),
    fbp,
    fbc
  };
};

export const rememberPendingCheckoutTracking = (
  tracking: Omit<PendingCheckoutTracking, "createdAt">
): void => {
  if (!config.enableMarketing) return;
  const storage = getSessionStorage();
  if (!storage) {
    return;
  }

  try { storage.setItem(
    PENDING_CHECKOUT_STORAGE_KEY,
    JSON.stringify({
      ...tracking,
      createdAt: Date.now()
    } satisfies PendingCheckoutTracking)
  ); } catch { /* Storage must never block payment. */ }
};

export const getPendingCheckoutTracking = (
  expectedSessionId?: string
): PendingCheckoutTracking | null => {
  const pendingTracking = readPendingCheckoutTracking();
  if (!pendingTracking) {
    return null;
  }

  if (
    expectedSessionId &&
    pendingTracking.sessionId.trim().length > 0 &&
    pendingTracking.sessionId !== expectedSessionId
  ) {
    return null;
  }

  return pendingTracking;
};

export const trackMetaLead = (eventId?: string): void => {
  const fbq = getFbq();
  if (!fbq) return;
  if (eventId) {
    fbq("track", "Lead", {}, { eventID: eventId });
  } else {
    fbq("track", "Lead");
  }
};

const sendMetaWebsiteEventToConversionsApi = (
  eventName: MetaWebsiteEventName,
  eventId: string
): boolean => {
  const normalizedEventId = normalizeEventId(eventId);
  if (!normalizedEventId) {
    return false;
  }

  const trackingContext = getMetaTrackingContext();
  sendMetaWebsiteEvent({
    eventName,
    eventId: normalizedEventId,
    externalId: trackingContext.externalId,
    fbp: trackingContext.fbp,
    fbc: trackingContext.fbc
  });

  return true;
};

const trackMetaWebsiteEvent = (eventName: MetaWebsiteEventName): string => {
  const eventId = generateEventId();
  const fbq = getFbq();

  if (fbq) {
    fbq("track", eventName, {}, { eventID: eventId });
  }

  sendMetaWebsiteEventToConversionsApi(eventName, eventId);
  return eventId;
};

export const sendMetaPageViewToConversionsApi = (eventId: string): boolean => {
  return sendMetaWebsiteEventToConversionsApi("PageView", eventId);
};

export const trackMetaViewContent = (): string => {
  return trackMetaWebsiteEvent("ViewContent");
};

/** Browser and CAPI use the same milestone ID so Meta can deduplicate them. */
export const trackMetaMilestone = (eventName: MetaCustomEventName | "ViewContent", eventId: string, contentName?: MetaContentName): void => {
  if (!config.enableMarketing) return;
  try {
    getFbq()?.(eventName === "ViewContent" ? "track" : "trackCustom", eventName,
      contentName ? { content_name: contentName } : {}, { eventID: eventId });
  } catch { /* A blocked pixel must not prevent server delivery. */ }
  const { externalId, fbp, fbc } = getMetaTrackingContext();
  sendMetaWebsiteEvent({ eventName, eventId, externalId, fbp, fbc, contentName });
};

/** One browser/server pair per checkout attempt, including retries and reopened fields. */
export const trackMetaInitiateCheckout = (event: MetaPixelPurchaseEvent & { promoCode?: string }): boolean => {
  if (!config.enableMarketing) return false;
  const eventId = event.eventId.trim();
  if (!eventId || !event.contentId.trim() || !/^[A-Z]{3}$/.test(event.currency) ||
      !Number.isFinite(event.value) || event.value < 0) return false;
  const previous = readTrackedEventIds(TRACKED_CHECKOUT_EVENT_IDS_STORAGE_KEY);
  if (trackedCheckoutIds.has(eventId) || previous.includes(eventId)) return true;
  trackedCheckoutIds.add(eventId);
  try {
    getSessionStorage()?.setItem(TRACKED_CHECKOUT_EVENT_IDS_STORAGE_KEY, JSON.stringify([...previous, eventId].slice(-200)));
  } catch { /* In-memory deduplication remains available. */ }
  try {
    getFbq()?.("track", "InitiateCheckout", {
      content_ids: [event.contentId], content_type: "product", currency: event.currency, value: event.value,
    }, { eventID: eventId });
  } catch { /* Browser pixel errors must not prevent server delivery or checkout. */ }
  const { externalId, fbp, fbc } = getMetaTrackingContext();
  sendMetaWebsiteEvent({ eventName: "InitiateCheckout", eventId, externalId, fbp, fbc,
    contentId: event.contentId, currency: event.currency, value: event.value, promoCode: event.promoCode });
  return true;
};

export const trackMetaPurchase = ({
  eventId,
  contentId,
  currency,
  value
}: MetaPixelPurchaseEvent): boolean => {
  const normalizedEventId = eventId.trim();
  if (!normalizedEventId || !contentId.trim() || !currency.trim() || !Number.isFinite(value)) {
    return false;
  }

  if (wasPurchaseEventTracked(normalizedEventId)) {
    return true;
  }

  const fbq = getFbq();
  if (!fbq) {
    return false;
  }

  fbq(
    "track",
    "Purchase",
    {
      content_ids: [contentId],
      content_type: "product",
      currency,
      value
    },
    {
      eventID: normalizedEventId
    }
  );

  markPurchaseEventTracked(normalizedEventId);
  return true;
};
